import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo, Fragment } from "react";
import styled from "styled-components";
import { FormattedMessage } from "react-intl";
import tinytime from "tinytime";
import { Text, Flex, Box } from "sprout-ui-primitives";
import { getTemplates } from "sprout-lib/dist/activities";
import slugify from "sprout-lib/dist/slugify";
import { time as titleTime } from "@sprout/client-components/dist/TimeAgo";
import { Card, CardContent as BaseCardContent, CardLink } from "@sprout/client-components/dist/Card";
import { Link } from "react-router-dom";
import Avatar from "@sprout/client-components/dist/Avatar";
import { getItemUrl } from "sprout-lib";
import useCurrentUser from "@sprout/client-graphql/dist/useCurrentUser";
import CommentText from "./CommentText";
import From from "./From";
import ActivityMetadata from "./Activity_Metadata";
import ActivityTodo from "./ActivityTodo";
export const time = tinytime("{h}:{mm}{a}");
const CardContent = styled(BaseCardContent)`
  display: flex;
`;
const TextWrapper = styled(Flex)`
  flex-direction: column;
  justify-content: center;
  min-width: 0;
  flex: auto;
  margin-left: 16px;
`;
const Time = styled(Text).attrs({
    as: "span"
})`
  pointer-events: auto;
`;
const StyledFlex = styled(Flex)`
  font-size: 12px;
  line-height: 16px;
  white-space: pre;

  a:hover {
    text-decoration: underline;
  }
`;
const StyledFlex2 = styled(Flex)`
  font-size: 12px;
  line-height: 16px;
  white-space: pre;

  a:hover {
    text-decoration: underline;
  }
`;
const StyledTime2 = styled(Time)`
  margin-left: auto;
`;
const StyledTime = styled(Time)`
  margin-left: auto;
`;
const isDev = process.env.NODE_ENV === "development";
const DevelopmentOnlyActivity = ({ activity  })=>{
    if (!isDev) return null;
    const { actor , oldTopic , notificationType , rootPost , rootTopic , createdAt  } = activity;
    const parentTopic = oldTopic || rootPost && rootPost.parentTopic || rootTopic;
    return /*#__PURE__*/ _jsx(Card, {
        style: {
            background: `repeating-linear-gradient(
          -55deg,
          #ffc,
          #ffc 10px,
          #e8e8e8 10px,
          #e8e8e8 20px
        )`
        },
        children: /*#__PURE__*/ _jsxs(CardContent, {
            children: [
                actor && /*#__PURE__*/ _jsx(Box, {
                    flex: "none",
                    children: /*#__PURE__*/ _jsx(Avatar, {
                        user: actor,
                        size: 32,
                        navigable: true
                    })
                }),
                /*#__PURE__*/ _jsxs(TextWrapper, {
                    children: [
                        /*#__PURE__*/ _jsxs(StyledFlex, {
                            color: "black.60",
                            children: [
                                "THIS APPEARS IN DEVELOPMENT ONLY!",
                                " ",
                                parentTopic && parentTopic.rootTopicOrSelf && parentTopic.rootTopicOrSelf.owner && /*#__PURE__*/ _jsx(Link, {
                                    to: `/${parentTopic.rootTopicOrSelf.owner.username}/${parentTopic.id}-${slugify(parentTopic.title)}`,
                                    children: parentTopic.title
                                }),
                                /*#__PURE__*/ _jsx(StyledTime, {
                                    title: titleTime.render(new Date(createdAt)),
                                    children: time.render(new Date(createdAt)).toLowerCase()
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx(Text, {
                            fontSize: 2,
                            fontWeight: "bold",
                            lineHeight: "20px",
                            children: notificationType
                        })
                    ]
                })
            ]
        })
    });
};
const Comments = ({ activities  })=>/*#__PURE__*/ _jsx(_Fragment, {
        children: activities.map(({ comment , nodeId  })=>/*#__PURE__*/ _jsxs(Fragment, {
                children: [
                    comment && /*#__PURE__*/ _jsx(CommentText, {
                        text: comment.text
                    }),
                    !comment && /*#__PURE__*/ _jsx(Text, {
                        color: "grey.400",
                        fontSize: 1,
                        lineHeight: "20px",
                        mt: "8px",
                        children: "This comment is no longer available"
                    })
                ]
            }, nodeId))
    });
/**
 * always show checked state for `COMPLETED_CHECKLIST_ITEM`,
 * and not the current state
 */ const ALWAYS_ON_TYPES = [
    "COMPLETED_CHECKLIST_ITEM"
];
const ALWAYS_OFF_TYPES = [
    "ADDED_ITEM_TO_POST",
    "UNCOMPLETED_CHECKLIST_ITEM"
];
const ChecklistItems = ({ activities  })=>/*#__PURE__*/ _jsx(_Fragment, {
        children: activities.map(({ notificationType , item  })=>{
            if (!item) return null;
            if (item.type !== "CHECKLIST_ITEM") return null;
            let completed;
            if (ALWAYS_ON_TYPES.includes(notificationType)) completed = true;
            else if (ALWAYS_OFF_TYPES.includes(notificationType)) completed = false;
            else ({ completed  } = item);
            return /*#__PURE__*/ _jsx(ActivityTodo, {
                item: item,
                checked: completed
            }, item.nodeId);
        })
    });
export const Activity = (props)=>{
    const { activity , moreActivities , subject  } = props;
    const { actor , item , oldTopic , newTopic , notificationType , comment , rootPost , rootTopic , createdAt ,  } = activity;
    const allActivities = useMemo(()=>[
            activity,
            ...moreActivities || []
        ], [
        activity,
        moreActivities
    ]);
    const currentUser = useCurrentUser();
    const TEMPLATES = getTemplates(currentUser);
    const templateFn = TEMPLATES[notificationType];
    if (!templateFn) // This can only happen when NODE_ENV === "development" thanks to the check in `condition.js`
    return /*#__PURE__*/ _jsx(DevelopmentOnlyActivity, {
        activity: activity
    });
    const template = templateFn(activity, {
        subject
    });
    /**
   * Using fancy fallback here.
   * TODO: explain
   */ const metadata = item || rootPost;
    const parentTopic = oldTopic || rootPost && rootPost.parentTopic || rootTopic;
    return /*#__PURE__*/ _jsxs(Card, {
        children: [
            template.url ? /*#__PURE__*/ _jsx(CardLink, {
                to: template.url
            }) : null,
            /*#__PURE__*/ _jsxs(CardContent, {
                children: [
                    actor && /*#__PURE__*/ _jsx(Box, {
                        flex: "none",
                        children: /*#__PURE__*/ _jsx(Avatar, {
                            user: actor,
                            size: 32,
                            navigable: true
                        })
                    }),
                    /*#__PURE__*/ _jsxs(TextWrapper, {
                        children: [
                            /*#__PURE__*/ _jsxs(StyledFlex2, {
                                color: "black.60",
                                children: [
                                    ![
                                        "MOVED_ITEM",
                                        "TRANSFERRED_ITEM_IN",
                                        "TRANSFERRED_ITEM_OUT", 
                                    ].includes(notificationType) && parentTopic && parentTopic.rootTopicOrSelf && parentTopic.rootTopicOrSelf.owner && /*#__PURE__*/ _jsx(Link, {
                                        to: getItemUrl(parentTopic, parentTopic.rootTopicOrSelf.owner),
                                        children: parentTopic.title
                                    }),
                                    [
                                        "MOVED_ITEM",
                                        "TRANSFERRED_ITEM_IN",
                                        "TRANSFERRED_ITEM_OUT", 
                                    ].includes(notificationType) && /*#__PURE__*/ _jsx(From, {
                                        oldTopic: oldTopic,
                                        newTopic: newTopic
                                    }),
                                    /*#__PURE__*/ _jsx(StyledTime2, {
                                        title: titleTime.render(new Date(createdAt)),
                                        children: time.render(new Date(createdAt)).toLowerCase()
                                    }),
                                    notificationType === "COMMENTED" && comment && comment.editedAt && /*#__PURE__*/ _jsx(Time, {
                                        title: titleTime.render(new Date(comment.editedAt)),
                                        children: " (edited)"
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx(Text, {
                                fontSize: 2,
                                fontWeight: "bold",
                                lineHeight: "20px",
                                wordBreak: "break-word",
                                children: /*#__PURE__*/ _jsx(FormattedMessage, {
                                    id: notificationType,
                                    defaultMessage: template.template,
                                    values: template.variables
                                })
                            }),
                            metadata && metadata.url && /*#__PURE__*/ _jsx(ActivityMetadata, {
                                type: metadata.type,
                                ogUrl: metadata.url,
                                ogTitle: metadata.linkTitle || undefined,
                                ogImage: metadata.linkImage || undefined
                            }),
                            notificationType === "COMMENTED" && /*#__PURE__*/ _jsx(Comments, {
                                activities: allActivities
                            }),
                            [
                                "ADDED_ITEM_TO_POST",
                                "COMPLETED_CHECKLIST_ITEM",
                                "UNCOMPLETED_CHECKLIST_ITEM", 
                            ].includes(notificationType) && /*#__PURE__*/ _jsx(ChecklistItems, {
                                activities: allActivities
                            })
                        ]
                    })
                ]
            })
        ]
    });
};
export default Activity;
